import React, { useEffect, useMemo } from 'react';

import { Handle, Position } from '@xyflow/react';

import { ConditionalSplitIcon } from 'assets/icons/conditional-split-icon';

import { conditionalSchema } from '../../../summary-sidebar/sections/conditional-section';
import { useWorkflowBuilderContext } from '../../../wizard/builder-context';
import { conditionalTypeLabels, type ConditionalNodeData } from '../models';
import { AddNodeHandle } from '../shared/add-node-handle';

import { NodeActions } from './node-actions';
import { NodeRowContainer, StyledBaseNode, StyledDescription, StyledDivider, StyledLabel } from './styles';

type Props = {
  data: ConditionalNodeData;
  id: string;
};

export function CustomConditionalNode({ data, id }: Props) {
  const [builderState] = useWorkflowBuilderContext();
  const { selectedNode, isReadOnly } = builderState;
  const isCurrentlySelectedNode = selectedNode?.id === id;
  const { edges } = builderState;

  const hasOutgoingYesConnection = edges.some((edge) => edge.source === id && edge.edgeType === 'yes');
  const hasOutgoingNoConnection = edges.some((edge) => edge.source === id && edge.edgeType === 'no');

  const isValid = useMemo(() => conditionalSchema.isValidSync(data), [data]);

  const { stats } = data;

  useEffect(() => {
    if (data.reportIsValid) {
      data.reportIsValid(isValid);
    }
  }, [isValid, data]);

  let conditionalTypeLabel = conditionalTypeLabels[data.conditionalType];

  if (data.conditionalType === 'isInSegment') {
    const { segmentIds } = data.config;
    const segmentCount = segmentIds?.length ?? 0;
    conditionalTypeLabel = `Is in segment (${segmentCount} selected segments)`;
  }

  return (
    <StyledBaseNode isError={!isValid} isSelected={isCurrentlySelectedNode}>
      <Handle position={Position.Top} type='target' />
      <NodeRowContainer>
        <ConditionalSplitIcon />
        <StyledLabel>Conditional</StyledLabel>
      </NodeRowContainer>

      <StyledDescription>{conditionalTypeLabel}</StyledDescription>
      {!isReadOnly && <StyledDivider variant='compact' />}

      <NodeActions nodeId={id} nodeType={data.nodeType} showAnalytics={data.showAnalytics} stats={stats} />
      {data.onAddNode && !hasOutgoingYesConnection && (
        <AddNodeHandle type='yes' onAdd={(nodeType) => data.onAddNode!(id, nodeType, 'yes')} />
      )}
      {data.onAddNode && !hasOutgoingNoConnection && (
        <AddNodeHandle type='no' onAdd={(nodeType) => data.onAddNode!(id, nodeType, 'no')} />
      )}
      <Handle position={Position.Bottom} type='source' />
    </StyledBaseNode>
  );
}
